import "./style.scss";
import { FC, useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import CustomSwitch from "@/src/components/CustomUtils/CustomSwitch/CustomSwitch";
import { AOPMaltaCertificateData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import { TINYMCE_EDITOR_CONTENT_STYLE, TINYMCE_EDITOR_HEIGHT } from "../../../tinyMceEditorDisplayStyle";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import AOPTaskIdInput from "@/src/components/AdmissionsOfficePage/shared/AOPTaskIdInput/AOPTaskIdInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { useAdmissionTaskPrefill } from "@/src/hooks/useAdmissionTaskPrefill";
import { certificateTaskPrefillMapping } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import { CERTIFICATE_BASIC_REGISTRY, type CertificateBasicCountry } from "../certificateBasicRegistry";

interface IAOPFormValues {
  whose_doc: string;
  currentDate: string;
  certificate: string;
  fullName: string;
  body: string;
  task_id?: string;
  withQR?: boolean;
}

interface AOPConfigProps {
  country: CertificateBasicCountry;
  dataForGeneration: AOPMaltaCertificateData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaCertificateData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaCertificateData>>;
  setCertificateInfo: React.Dispatch<string>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({
  country,
  dataForGeneration,
  setDataForGeneration,
  setBodyForPrev,
  setCertificateInfo,
}) => {
  const { certificateTemplates } = CERTIFICATE_BASIC_REGISTRY[country];
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      body: "",
      currentDate: formatDateForInput(today),
      withQR: true,
    },
  });

  const dispatch = useDispatch();
  const [certificateContent, setCertificateContent] = useState<string>("");

  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const generatedDoc = useAdmissionGeneratedDocumentActions({ getValues });

  const certificate = watch("certificate");

  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);
  const certificateAuto = useAutocomplete(Object.keys(certificateTemplates));

  const { applyTaskPrefill, lastPrefilledTaskIdRef } = useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country,
    documentSlug: "certificates",
    mapping: certificateTaskPrefillMapping,
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
    },
  });

  useEffect(() => {
    if (!certificate) {
      setCertificateContent("");
      return;
    }

    const key = certificate as keyof typeof certificateTemplates;
    const certificateSettings = certificateTemplates[key];

    if (certificateSettings) {
      setCertificateContent(certificateSettings.tinyMCEContent);
    } else {
      setCertificateContent("");
    }
  }, [certificate, certificateTemplates]);

  const generateNewDocument = async () => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      let response;
      if (certificate === "Certificate") {
        response = await admissionOfficeRequests.createCertificateDoc(dataForGeneration);
      } else {
        response = await admissionOfficeRequests.createAuthorizationLetterteDoc(dataForGeneration);
      }
      generatedDoc.onDocumentGenerated(response);
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      whose_doc: watch("whose_doc"),
      currentDate: watch("currentDate"),
      fullName: watch("fullName"),
      withQR: watch("withQR"),
    };
    setDataForGeneration((prev) => ({
      ...prev,
      whose_doc: watchedFields.whose_doc,
      need_qr: !!watchedFields.withQR,
      currentDate: watchedFields.currentDate,
      fullName: watchedFields.fullName,
      body: splitHtmlByBlocks(certificateContent),
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      whose_doc: watchedFields.whose_doc,
      currentDate: watchedFields.currentDate,
      fullName: watchedFields.fullName,
      body: splitHtmlByBlocksWithFontConvert(certificateContent),
    }));
    if (certificateContent.trim().length > 0) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
      setCertificateInfo(certificate);
    }
  };

  const handleCertificateEditorChange = (newCertificateContent: string) => setCertificateContent(newCertificateContent);

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as keyof IAOPFormValues);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(String(value));
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">CERTIFICATE</p>
        <form className="admission-office-page-config-form">
          <div className="form-group-fields">
            <div className="form-group-fields-input whose_doc">
              <AutocompleteInput
                placeholder="Whose"
                value={whoseDocAuto.query}
                onChange={whoseDocAuto.onChange}
                onSelect={(v) => {
                  whoseDocAuto.onSelect(v);
                  setValue("whose_doc", v);
                }}
                isOpen={whoseDocAuto.isOpen}
                options={whoseDocAuto.filtered}
                error={!!errors.body}
                  inputProps={{
                  register: register("whose_doc"),
                  close: whoseDocAuto.close,
                  setOpen: whoseDocAuto.onClick,
                  onArrowClick: whoseDocAuto.onArrowClick,
                }}
                identifier={"whose"}
              />
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Type of certificate"
                value={certificateAuto.query}
                onChange={certificateAuto.onChange}
                onSelect={(v) => {
                  certificateAuto.onSelect(v);
                  setValue("certificate", v);
                }}
                isOpen={certificateAuto.isOpen}
                options={certificateAuto.filtered}
                error={!!errors.certificate}
                  inputProps={{
                  register: register("certificate"),
                  close: certificateAuto.close,
                  setOpen: certificateAuto.onClick,
                  onArrowClick: certificateAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "certificate",
                }}
                identifier={"certificate"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <label>Certificate body</label>
          <Editor
            value={certificateContent}
            init={{
              height: TINYMCE_EDITOR_HEIGHT,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: TINYMCE_EDITOR_CONTENT_STYLE,
            }}
            onEditorChange={handleCertificateEditorChange}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />
          <AOPGeneratedDocumentToolbar
            isGenerated={generatedDoc.isGenerated}
            generatedPdfUrl={generatedDoc.generatedPdfUrl}
            taskAttachedPdfUrl={generatedDoc.taskAttachedPdfUrl}
            previewPdfUrl={generatedDoc.previewPdfUrl}
            canOpenPdfPreview={generatedDoc.canOpenPdfPreview}
            canOpenTaskDocumentEye={generatedDoc.canOpenTaskDocumentEye}
            isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
            setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
            generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
            onDownload={generatedDoc.handleDownloadGeneratedPdf}
            onApprove={generatedDoc.handlePreviewApprove}
            onReject={generatedDoc.handlePreviewReject}
            isTaskDocumentCheckReview={generatedDoc.isTaskDocumentCheckReview}
            previewButton={
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
            }
            generateButton={
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
            }
          />
          <div className="form-group-fields">
            <AOPTaskIdInput
              register={register}
              setError={setError}
              clearErrors={clearErrors}
              applyTaskPrefill={applyTaskPrefill}
              lastPrefilledTaskIdRef={lastPrefilledTaskIdRef}
              onCopy={() => handleCopy("task_id")}
            />
            <div className="form-group-fields-invoice">
              <Controller
                name="withQR"
                control={control}
                render={({ field }) => <CustomSwitch label="With QR" value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
